import { useState, useMemo } from "react";
import { VenueDetailsBySlugResponse, useAuth, useI18N } from "common/hooks";
import { Search, X } from "lucide-react";
import FloorNotesContent from "./FloorNotesContent";
import ToDoTaskContent from "./ToDoTaskContent";
import ProjectInformationContent from "./ProjectInformationContent";
import PublicContent from "./PublicContent";
import { StandardButton } from "../common/StandardButton";
import { backendUrl } from "common/config";
import AnalyticsService from "@/services/analytics";

type AccessibilityAlertsTabProps = {
  venueDetailsResponse: VenueDetailsBySlugResponse | undefined;
  onRefresh?: () => void;
  pageName?: string; // For analytics tracking
};

type AlertTabType =
  | "floor-note"
  | "todo-task"
  | "project-information"
  | "public";

function AccessibilityAlertsTab({
  venueDetailsResponse,
  onRefresh,
  pageName = "Alerts Tab",
}: AccessibilityAlertsTabProps) {
  const i18n = useI18N();
  const { isLoggedIn, user } = useAuth();

  // Determine event names based on pageName
  const getEventName = (baseName: string) => {
    if (pageName === "Accessibility Space Guide") {
      const eventMap: Record<string, string> = {
        "alertsTabFloorNoteClicked": "accessibilitySpaceGuideAlertsTabSelected",
        "alertsTabTodoTaskClicked": "accessibilitySpaceGuideAlertsTabSelected",
        "alertsTabProjectInfoClicked": "accessibilitySpaceGuideAlertsTabSelected",
        "alertsTabPublicClicked": "accessibilitySpaceGuideAlertsTabSelected",
        "alertsTabSearchPerformed": "accessibilitySpaceGuideAlertsSearchPerformed",
      };
      return eventMap[baseName] || baseName;
    }
    return baseName;
  };
  const showMapperView =
    isLoggedIn &&
    (user?.roles?.isSociabilityMapper || user?.roles?.isSociabilityTeam);

  const [activeTab, setActiveTab] = useState<AlertTabType>(
    showMapperView ? "floor-note" : "public",
  );
  const [searchTerm, setSearchTerm] = useState("");

  // Calculate note counts for each tab
  const noteCounts = useMemo(() => {
    if (!venueDetailsResponse) {
      return {
        floorNotes: 0,
        todoTasks: 0,
        projectInfo: 0,
        publicNotes: 0,
      };
    }

    const { businessNotesForMappers, enterpriseBusinessNotesActions } =
      venueDetailsResponse;
    const businessNotesAll = venueDetailsResponse?.details?.businessNotesAll;

    // Get floor-type notes count
    const floorActionIds =
      enterpriseBusinessNotesActions
        ?.filter((action) => action.children_business_notes_action.length === 0)
        .map((action) => action.id) || [];
    const floorNotesCount =
      businessNotesForMappers?.filter((note) =>
        floorActionIds.includes(note.business_notes_action_id),
      ).length || 0;

    // Get task-based notes count (showTasks === 1)
    const taskActionIds: number[] = [];
    enterpriseBusinessNotesActions?.forEach((action) => {
      if (action.children_business_notes_action) {
        action.children_business_notes_action.forEach((childAction) => {
          if (childAction.show_tasks === 1) {
            taskActionIds.push(childAction.id);
          }
        });
      }
    });
    const todoTasksCount =
      businessNotesForMappers?.filter((note) =>
        taskActionIds.includes(note.business_notes_action_id),
      ).length || 0;

    // Get project information notes count (showTasks === 0)
    const projectInfoActionIds: number[] = [];
    enterpriseBusinessNotesActions?.forEach((action) => {
      if (action.children_business_notes_action) {
        action.children_business_notes_action.forEach((childAction) => {
          if (childAction.show_tasks === 0) {
            projectInfoActionIds.push(childAction.id);
          }
        });
      }
    });
    const projectInfoCount =
      businessNotesForMappers?.filter((note) =>
        projectInfoActionIds.includes(note.business_notes_action_id),
      ).length || 0;

    // Get public notes count
    const publicNotesCount = businessNotesAll?.length || 0;

    return {
      floorNotes: floorNotesCount,
      todoTasks: todoTasksCount,
      projectInfo: projectInfoCount,
      publicNotes: publicNotesCount,
    };
  }, [venueDetailsResponse]);

  const allTabs = [
    {
      id: "floor-note" as AlertTabType,
      label: `${i18n.get("floorNote")} (${noteCounts.floorNotes})`,
      mapperOnly: true,
    },
    {
      id: "todo-task" as AlertTabType,
      label: `${i18n.get("todoTask")} (${noteCounts.todoTasks})`,
      mapperOnly: true,
    },
    {
      id: "project-information" as AlertTabType,
      label: `${i18n.get("projectInformation")} (${noteCounts.projectInfo})`,
      mapperOnly: true,
    },
    {
      id: "public" as AlertTabType,
      label: `${i18n.get("public")} (${noteCounts.publicNotes})`,
      mapperOnly: false,
    },
  ];

  const tabs = allTabs.filter((tab) => !tab.mapperOnly || showMapperView);

  const renderContent = () => {
    switch (activeTab) {
      case "floor-note":
        return showMapperView ? (
          <FloorNotesContent
            venueDetailsResponse={venueDetailsResponse}
            searchTerm={searchTerm}
            onRefresh={onRefresh}
          />
        ) : (
          <PublicContent
            venueDetailsResponse={venueDetailsResponse}
            searchTerm={searchTerm}
          />
        );
      case "todo-task":
        return showMapperView ? (
          <ToDoTaskContent
            venueDetailsResponse={venueDetailsResponse}
            searchTerm={searchTerm}
            onRefresh={onRefresh}
          />
        ) : (
          <PublicContent
            venueDetailsResponse={venueDetailsResponse}
            searchTerm={searchTerm}
          />
        );
      case "project-information":
        return showMapperView ? (
          <ProjectInformationContent
            venueDetailsResponse={venueDetailsResponse}
            searchTerm={searchTerm}
            onRefresh={onRefresh}
          />
        ) : (
          <PublicContent
            venueDetailsResponse={venueDetailsResponse}
            searchTerm={searchTerm}
          />
        );
      case "public":
        return (
          <PublicContent
            venueDetailsResponse={venueDetailsResponse}
            searchTerm={searchTerm}
          />
        );
      default:
        return (
          <PublicContent
            venueDetailsResponse={venueDetailsResponse}
            searchTerm={searchTerm}
          />
        );
    }
  };

  return (
    <>
      <div className="h-screen flex flex-col">
        {/* Secondary Filter Tabs - Only shown for mapper view */}
        {showMapperView && (
          <nav
            className="bg-white border-b border-border-token-neutral-minimal px-4 py-4 md:px-6 lg:px-8 flex-shrink-0"
            aria-label="Alert filters"
          >
            <div className="flex flex-col md:flex-row gap-4 justify-between items-start md:items-center">
              <ul className="flex gap-4 md:gap-6 whitespace-nowrap overflow-x-auto">
                {tabs.map((tab) => (
                  <li key={tab.id}>
                    <button
                      onClick={() => {
                        setActiveTab(tab.id);
                        // Analytics tracking
                        if (tab.id === "floor-note") {
                          const eventName = getEventName("alertsTabFloorNoteClicked");
                          AnalyticsService.trackGAEventHandler(eventName);
                          AnalyticsService.trackMx(eventName, {});
                        } else if (tab.id === "todo-task") {
                          const eventName = getEventName("alertsTabTodoTaskClicked");
                          AnalyticsService.trackGAEventHandler(eventName);
                          AnalyticsService.trackMx(eventName, {});
                        } else if (tab.id === "project-information") {
                          const eventName = getEventName("alertsTabProjectInfoClicked");
                          AnalyticsService.trackGAEventHandler(eventName);
                          AnalyticsService.trackMx(eventName, {});
                        } else if (tab.id === "public") {
                          const eventName = getEventName("alertsTabPublicClicked");
                          AnalyticsService.trackGAEventHandler(eventName);
                          AnalyticsService.trackMx(eventName, {});
                        }
                      }}
                      className={`px-3 py-2 rounded-md focus-blue ${
                        activeTab === tab.id
                          ? "label-m-medium text-fg-brand-strong bg-container-brand-minimal"
                          : "label-m-medium text-fg-neutral-subtle hover:bg-container-neutral-minimal"
                      }`}
                    >
                      {tab.label}
                    </button>
                  </li>
                ))}
              </ul>

              {/* Search Bar and Add Alert Button */}
              <div className="flex flex-col md:flex-row md:items-center gap-1.5 md:ml-auto w-full md:w-auto">
                <div className="relative w-full md:w-64">
                  <label htmlFor="alerts-search" className="sr-only">
                    Search by space name
                  </label>
                  <input
                    id="alerts-search"
                    type="text"
                    placeholder={i18n.get("spaceName")}
                    value={searchTerm}
                    onChange={(e) => {
                      setSearchTerm(e.target.value);
                      const eventName = getEventName("alertsTabSearchPerformed");
                      AnalyticsService.trackGAEventHandler(eventName);
                      AnalyticsService.trackMx(eventName, { SearchTerm: e.target.value });
                    }}
                    className="w-full  h-12  px-4 py-2 pr-10 rounded-lg border border-border-token-neutral-minimal title-s-semibold text-fg-neutral-strong bg-bg-brand-secondary-default placeholder:text-fg-neutral-minimal focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                  />
                  {searchTerm ? (
                    <button
                      type="button"
                      onClick={() => setSearchTerm("")}
                      aria-label="Clear search"
                      className="absolute right-3 top-1/2 -translate-y-1/2 p-1 text-fg-neutral-minimal hover:text-fg-neutral-subtle focus-blue"
                    >
                      <X size={20} aria-hidden="true" />
                    </button>
                  ) : (
                    <Search
                      size={20}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-fg-neutral-minimal"
                      aria-hidden="true"
                    />
                  )}
                </div>

                {activeTab === "public" && (
                  <StandardButton
                    onClick={() => {
                      window.open(
                        `${backendUrl}/business/edit/${venueDetailsResponse?.details?.business?.id}?active_tab=accessibility&md=crtalert`,
                        "_blank",
                      );
                    }}
                    variant="icon"
                    size="icon-xlg"
                    className="px-3 py-1.5 label-m-medium font-medium border border-border-token-brand-minimal text-fg-neutral-subtle hover:bg-container-neutral-minimal rounded-md transition-colors disabled:opacity-50 disabled:cursor-not-allowed focus-blue"
                  >
                    <img
                      src="/images/PlusCircle.svg"
                      alt="Add alert"
                      className="mr-2 w-4 h-4"
                    />
                    Add alert
                  </StandardButton>
                )}
              </div>
            </div>
          </nav>
        )}

        {/* Main Content Area */}
        {renderContent()}
      </div>
    </>
  );
}

export default AccessibilityAlertsTab;
